/**
 * Permission categories for grouping in the UI
 */
export enum PermissionCategory {
  PlannerManagement = 'PlannerManagement',
  TeamManagement = 'TeamManagement',
  ProjectsManagement = 'ProjectsManagement',
  ClientsManagement = 'ClientsManagement',
  FinanceManagement = 'FinanceManagement',
  // ReportsManagement = 'ReportsManagement', // TODO: Uncomment when Reports feature is implemented
  SettingsManagement = 'SettingsManagement',
  TimeTrackingManagement = 'TimeTrackingManagement',
  TimeOffManagement = 'TimeOffManagement',
}

/**
 * Opsium Policy enum - represents all available permissions in the system
 * These values match the API response from /api/opsium-policies
 */
export enum OpsiumPolicy {
  // Full Access - grants all permissions
  FullAccess = 'FullAccess',

  // Assignments
  AssignmentsViewOwn = 'AssignmentsViewOwn',
  AssignmentsViewAll = 'AssignmentsViewAll',
  AssignmentsCreate = 'AssignmentsCreate',
  AssignmentsEdit = 'AssignmentsEdit',
  AssignmentsDelete = 'AssignmentsDelete',
  PlannerFinancialsView = 'PlannerFinancialsView',

  // Team Members
  TeamMembersViewAll = 'TeamMembersViewAll',
  TeamMembersCreate = 'TeamMembersCreate',
  TeamMembersEdit = 'TeamMembersEdit',
  TeamMembersDelete = 'TeamMembersDelete',

  // Access
  AccessGrantAndRevoke = 'AccessGrantAndRevoke',

  // Rates
  ExternalRatesManage = 'ExternalRatesManage',
  InternalRatesManage = 'InternalRatesManage',
  TeamMemberCapacityManage = 'TeamMemberCapacityManage',

  // Projects
  ProjectsViewAll = 'ProjectsViewAll',
  ProjectsCreate = 'ProjectsCreate',
  ProjectsEdit = 'ProjectsEdit',
  ProjectsDelete = 'ProjectsDelete',
  ProjectsBillingManage = 'ProjectsBillingManage',

  // Clients
  ClientsViewAll = 'ClientsViewAll',
  ClientsCreate = 'ClientsCreate',
  ClientsEdit = 'ClientsEdit',
  ClientsDelete = 'ClientsDelete',

  // Invoices and Bills
  InvoicesView = 'InvoicesView',
  BillsView = 'BillsView',
  InvoicesGenerate = 'InvoicesGenerate',
  BillsGenerate = 'BillsGenerate',
  InvoicesDelete = 'InvoicesDelete',
  BillsDelete = 'BillsDelete',

  // Project Overheads
  OverheadsView = 'OverheadsView',
  OverheadsManage = 'OverheadsManage',
  OverheadsDelete = 'OverheadsDelete',

  // Reports - TODO: Uncomment when Reports feature is implemented
  // CompanyFinanceReportView = 'CompanyFinanceReportView',
  // ProfitabilityAndTimeTrackingReport = 'ProfitabilityAndTimeTrackingReport',
  // ProfitabilityAndTimeTrackingReportLimited = 'ProfitabilityAndTimeTrackingReportLimited',
  // UtilizationReportView = 'UtilizationReportView',
  // UtilizationReportViewLimited = 'UtilizationReportViewLimited',

  // Account
  PersonalAccountViewAndEdit = 'PersonalAccountViewAndEdit',

  // Organization Management
  OrgStructureViewAndEdit = 'OrgStructureViewAndEdit',
  OrgSettingsManage = 'OrgSettingsManage',
  IntegrationsManage = 'IntegrationsManage',
  AccessAndPermissionsManage = 'AccessAndPermissionsManage',

  // Worklogs
  WorklogsView = 'WorklogsView',
  WorklogsCreate = 'WorklogsCreate',
  WorklogsEdit = 'WorklogsEdit',
  WorklogsDelete = 'WorklogsDelete',
  WorklogsApprovalsView = 'WorklogsApprovalsView',
  WorklogsApprove = 'WorklogsApprove',
  WorklogsEditOthers = 'WorklogsEditOthers',
  WorklogsDeleteOthers = 'WorklogsDeleteOthers',

  // Time-Off Management
  TimeOffTypesView = 'TimeOffTypesView',
  TimeOffTypesEdit = 'TimeOffTypesEdit',
  TimeOffsRequest = 'TimeOffsRequest',
  TimeOffsApprove = 'TimeOffsApprove',
  TimeOffsView = 'TimeOffsView',
  TimeOffsViewOthers = 'TimeOffsViewOthers',
  TimeOffsCancel = 'TimeOffsCancel',
  TimeOffsCancelOthers = 'TimeOffsCancelOthers',
  TimeOffsEdit = 'TimeOffsEdit',
  TimeOffsEditOthers = 'TimeOffsEditOthers',
  TimeOffBalancesEditOthers = 'TimeOffBalancesEditOthers',
}

// Inactive or invisible policies that should not be displayed in the UI
export const EXCLUDED_OPSIUM_POLICIES: OpsiumPolicy[] = [
  OpsiumPolicy.FullAccess,
  OpsiumPolicy.IntegrationsManage,
  OpsiumPolicy.AssignmentsViewOwn,
  OpsiumPolicy.WorklogsEditOthers,
  OpsiumPolicy.WorklogsDeleteOthers,
];

// All policies belonging to the Project Overheads feature — excluded when showOverheads flag is off
export const OVERHEADS_OPSIUM_POLICIES: OpsiumPolicy[] = [
  OpsiumPolicy.OverheadsView,
  OpsiumPolicy.OverheadsManage,
  OpsiumPolicy.OverheadsDelete,
];

// All policies belonging to the Time-Off feature — excluded when showTimeOffs flag is off
export const TIME_OFF_OPSIUM_POLICIES: OpsiumPolicy[] = [
  OpsiumPolicy.TimeOffTypesView,
  OpsiumPolicy.TimeOffTypesEdit,
  OpsiumPolicy.TimeOffsRequest,
  OpsiumPolicy.TimeOffsApprove,
  OpsiumPolicy.TimeOffsView,
  OpsiumPolicy.TimeOffsViewOthers,
  OpsiumPolicy.TimeOffsCancel,
  OpsiumPolicy.TimeOffsCancelOthers,
  OpsiumPolicy.TimeOffsEdit,
  OpsiumPolicy.TimeOffBalancesEditOthers,
  OpsiumPolicy.TimeOffsEditOthers,
];

export type ExcludedOpsiumPolicy = (typeof EXCLUDED_OPSIUM_POLICIES)[number];

/**
 * Defines which policies require another policy to be enabled first.
 * Key: dependent policy (Create/Edit/Delete/etc.)
 * Value: required policy (View) that must be enabled
 */
export const POLICY_DEPENDENCIES: Partial<Record<OpsiumPolicy, OpsiumPolicy>> = {
  // Assignments
  [OpsiumPolicy.AssignmentsCreate]: OpsiumPolicy.AssignmentsViewAll,
  [OpsiumPolicy.AssignmentsEdit]: OpsiumPolicy.AssignmentsViewAll,
  [OpsiumPolicy.AssignmentsDelete]: OpsiumPolicy.AssignmentsViewAll,
  [OpsiumPolicy.PlannerFinancialsView]: OpsiumPolicy.AssignmentsViewAll,

  // Team Members
  [OpsiumPolicy.TeamMembersCreate]: OpsiumPolicy.TeamMembersViewAll,
  [OpsiumPolicy.TeamMembersEdit]: OpsiumPolicy.TeamMembersViewAll,
  [OpsiumPolicy.TeamMembersDelete]: OpsiumPolicy.TeamMembersViewAll,
  [OpsiumPolicy.AccessGrantAndRevoke]: OpsiumPolicy.TeamMembersViewAll,

  // Projects
  [OpsiumPolicy.ProjectsCreate]: OpsiumPolicy.ProjectsViewAll,
  [OpsiumPolicy.ProjectsEdit]: OpsiumPolicy.ProjectsViewAll,
  [OpsiumPolicy.ProjectsDelete]: OpsiumPolicy.ProjectsViewAll,
  [OpsiumPolicy.ProjectsBillingManage]: OpsiumPolicy.ProjectsViewAll,

  // Clients
  [OpsiumPolicy.ClientsCreate]: OpsiumPolicy.ClientsViewAll,
  [OpsiumPolicy.ClientsEdit]: OpsiumPolicy.ClientsViewAll,
  [OpsiumPolicy.ClientsDelete]: OpsiumPolicy.ClientsViewAll,

  // Invoices
  [OpsiumPolicy.InvoicesGenerate]: OpsiumPolicy.InvoicesView,
  [OpsiumPolicy.InvoicesDelete]: OpsiumPolicy.InvoicesView,

  // Bills
  [OpsiumPolicy.BillsGenerate]: OpsiumPolicy.BillsView,
  [OpsiumPolicy.BillsDelete]: OpsiumPolicy.BillsView,

  // Project Overheads
  [OpsiumPolicy.OverheadsManage]: OpsiumPolicy.OverheadsView,
  [OpsiumPolicy.OverheadsDelete]: OpsiumPolicy.OverheadsView,

  // Rates
  [OpsiumPolicy.ExternalRatesManage]: OpsiumPolicy.TeamMembersEdit,
  [OpsiumPolicy.InternalRatesManage]: OpsiumPolicy.TeamMembersEdit,
  [OpsiumPolicy.TeamMemberCapacityManage]: OpsiumPolicy.TeamMembersEdit,

  // Worklogs
  [OpsiumPolicy.WorklogsCreate]: OpsiumPolicy.WorklogsView,
  [OpsiumPolicy.WorklogsEdit]: OpsiumPolicy.WorklogsView,
  [OpsiumPolicy.WorklogsDelete]: OpsiumPolicy.WorklogsView,
  [OpsiumPolicy.WorklogsApprove]: OpsiumPolicy.WorklogsApprovalsView,
  [OpsiumPolicy.WorklogsEditOthers]: OpsiumPolicy.WorklogsEdit,

  // Time-Off Management
  [OpsiumPolicy.TimeOffTypesEdit]: OpsiumPolicy.TimeOffTypesView,
  [OpsiumPolicy.TimeOffsCancel]: OpsiumPolicy.TimeOffsView,
  [OpsiumPolicy.TimeOffsCancelOthers]: OpsiumPolicy.TimeOffsViewOthers,
  [OpsiumPolicy.TimeOffsEdit]: OpsiumPolicy.TimeOffsView,
  [OpsiumPolicy.TimeOffsEditOthers]: OpsiumPolicy.TimeOffsViewOthers,
  [OpsiumPolicy.TimeOffBalancesEditOthers]: OpsiumPolicy.TimeOffsViewOthers,
};

/**
 * Reverse of POLICY_DEPENDENCIES.
 * Key: required policy (View)
 * Value: list of policies that depend on it (Create/Edit/Delete/etc.)
 */
export const POLICY_DEPENDENTS: Partial<Record<OpsiumPolicy, OpsiumPolicy[]>> = Object.entries(
  POLICY_DEPENDENCIES
).reduce(
  (acc, [dependent, required]) => {
    if (!acc[required]) acc[required] = [];
    acc[required]!.push(dependent as OpsiumPolicy);
    return acc;
  },
  {} as Partial<Record<OpsiumPolicy, OpsiumPolicy[]>>
);

/**
 * Maps each policy to its category for UI grouping
 */
export const POLICY_CATEGORIES: Record<
  Exclude<OpsiumPolicy, ExcludedOpsiumPolicy>,
  PermissionCategory
> = {
  // Planner Management
  [OpsiumPolicy.AssignmentsViewAll]: PermissionCategory.PlannerManagement,
  [OpsiumPolicy.AssignmentsCreate]: PermissionCategory.PlannerManagement,
  [OpsiumPolicy.AssignmentsEdit]: PermissionCategory.PlannerManagement,
  [OpsiumPolicy.AssignmentsDelete]: PermissionCategory.PlannerManagement,
  [OpsiumPolicy.PlannerFinancialsView]: PermissionCategory.PlannerManagement,

  // Team Management
  [OpsiumPolicy.TeamMembersViewAll]: PermissionCategory.TeamManagement,
  [OpsiumPolicy.TeamMembersCreate]: PermissionCategory.TeamManagement,
  [OpsiumPolicy.TeamMembersEdit]: PermissionCategory.TeamManagement,
  [OpsiumPolicy.TeamMembersDelete]: PermissionCategory.TeamManagement,
  [OpsiumPolicy.AccessGrantAndRevoke]: PermissionCategory.TeamManagement,
  [OpsiumPolicy.ExternalRatesManage]: PermissionCategory.TeamManagement,
  [OpsiumPolicy.InternalRatesManage]: PermissionCategory.TeamManagement,
  [OpsiumPolicy.TeamMemberCapacityManage]: PermissionCategory.TeamManagement,

  // Projects Management
  [OpsiumPolicy.ProjectsViewAll]: PermissionCategory.ProjectsManagement,
  [OpsiumPolicy.ProjectsCreate]: PermissionCategory.ProjectsManagement,
  [OpsiumPolicy.ProjectsEdit]: PermissionCategory.ProjectsManagement,
  [OpsiumPolicy.ProjectsDelete]: PermissionCategory.ProjectsManagement,
  [OpsiumPolicy.ProjectsBillingManage]: PermissionCategory.ProjectsManagement,

  // Clients Management
  [OpsiumPolicy.ClientsViewAll]: PermissionCategory.ClientsManagement,
  [OpsiumPolicy.ClientsCreate]: PermissionCategory.ClientsManagement,
  [OpsiumPolicy.ClientsEdit]: PermissionCategory.ClientsManagement,
  [OpsiumPolicy.ClientsDelete]: PermissionCategory.ClientsManagement,

  // Finance Management
  [OpsiumPolicy.InvoicesView]: PermissionCategory.FinanceManagement,
  [OpsiumPolicy.BillsView]: PermissionCategory.FinanceManagement,
  [OpsiumPolicy.InvoicesGenerate]: PermissionCategory.FinanceManagement,
  [OpsiumPolicy.BillsGenerate]: PermissionCategory.FinanceManagement,
  [OpsiumPolicy.InvoicesDelete]: PermissionCategory.FinanceManagement,
  [OpsiumPolicy.BillsDelete]: PermissionCategory.FinanceManagement,
  [OpsiumPolicy.OverheadsView]: PermissionCategory.FinanceManagement,
  [OpsiumPolicy.OverheadsManage]: PermissionCategory.FinanceManagement,
  [OpsiumPolicy.OverheadsDelete]: PermissionCategory.FinanceManagement,

  // Reports Management - TODO: Uncomment when Reports feature is implemented
  // [OpsiumPolicy.CompanyFinanceReportView]: PermissionCategory.ReportsManagement,
  // [OpsiumPolicy.ProfitabilityAndTimeTrackingReport]: PermissionCategory.ReportsManagement,
  // [OpsiumPolicy.ProfitabilityAndTimeTrackingReportLimited]: PermissionCategory.ReportsManagement,
  // [OpsiumPolicy.UtilizationReportView]: PermissionCategory.ReportsManagement,
  // [OpsiumPolicy.UtilizationReportViewLimited]: PermissionCategory.ReportsManagement,

  // Settings Management
  [OpsiumPolicy.PersonalAccountViewAndEdit]: PermissionCategory.SettingsManagement,
  [OpsiumPolicy.OrgStructureViewAndEdit]: PermissionCategory.SettingsManagement,
  [OpsiumPolicy.OrgSettingsManage]: PermissionCategory.SettingsManagement,
  [OpsiumPolicy.AccessAndPermissionsManage]: PermissionCategory.SettingsManagement,

  // Time tracking management
  [OpsiumPolicy.WorklogsView]: PermissionCategory.TimeTrackingManagement,
  [OpsiumPolicy.WorklogsCreate]: PermissionCategory.TimeTrackingManagement,
  [OpsiumPolicy.WorklogsEdit]: PermissionCategory.TimeTrackingManagement,
  [OpsiumPolicy.WorklogsDelete]: PermissionCategory.TimeTrackingManagement,
  [OpsiumPolicy.WorklogsApprovalsView]: PermissionCategory.TimeTrackingManagement,
  [OpsiumPolicy.WorklogsApprove]: PermissionCategory.TimeTrackingManagement,
  [OpsiumPolicy.WorklogsEditOthers]: PermissionCategory.TimeTrackingManagement,

  // Time-Off Management
  [OpsiumPolicy.TimeOffTypesView]: PermissionCategory.TimeOffManagement,
  [OpsiumPolicy.TimeOffTypesEdit]: PermissionCategory.TimeOffManagement,
  [OpsiumPolicy.TimeOffsRequest]: PermissionCategory.TimeOffManagement,
  [OpsiumPolicy.TimeOffsApprove]: PermissionCategory.TimeOffManagement,
  [OpsiumPolicy.TimeOffsView]: PermissionCategory.TimeOffManagement,
  [OpsiumPolicy.TimeOffsViewOthers]: PermissionCategory.TimeOffManagement,
  [OpsiumPolicy.TimeOffsCancel]: PermissionCategory.TimeOffManagement,
  [OpsiumPolicy.TimeOffsCancelOthers]: PermissionCategory.TimeOffManagement,
  [OpsiumPolicy.TimeOffsEdit]: PermissionCategory.TimeOffManagement,
  [OpsiumPolicy.TimeOffBalancesEditOthers]: PermissionCategory.TimeOffManagement,
  [OpsiumPolicy.TimeOffsEditOthers]: PermissionCategory.TimeOffManagement,
};
